import { useEffect, useRef, useState } from "react";
import Markdown from "../chat/Markdown";
import ChatArtifacts from "../chat/ChatArtifacts";
import api, { API_BASE, fetchWithAuth, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, Paperclip, X, FileText } from "lucide-react";

const SUGGESTIONS = [
  "Find me flights SJU → DEN next month",
  "Plan a long weekend in Miami",
  "What trips do I have coming up?",
];

function Message({ msg, onProposalAction, onCancelAction, onActionAction }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="chat-message-user">
        <div className="max-w-[80%] bg-sand-950 text-white px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
          {msg.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {msg.attachments.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1 bg-surface/10 border border-white/20 px-2 py-0.5 text-[11px] font-mono" data-testid="message-attachment-chip">
                  <Paperclip size={10} /> {a.filename}
                </span>
              ))}
            </div>
          )}
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex" data-testid="chat-message-assistant">
      <div className="max-w-[92%] md:max-w-[85%] bg-surface rounded-card border border-hairline px-4 py-3">
        <Markdown text={msg.content} />
        <ChatArtifacts artifacts={msg.artifacts} messageId={msg.id}
          onProposalAction={(idx, action) => onProposalAction(msg.id, idx, action)}
          onCancelAction={(idx, action) => onCancelAction(msg.id, idx, action)}
          onActionAction={(idx, action) => onActionAction(msg.id, idx, action)} />
      </div>
    </div>
  );
}

function Draft({ draft }) {
  return (
    <div className="flex" data-testid="chat-draft">
      <div className="max-w-[92%] md:max-w-[85%] bg-surface rounded-card border border-hairline px-4 py-3 w-full md:w-auto">
        {draft.statuses.map((s, i) => (
          <p key={i} className="flex items-center gap-2 text-xs text-ember font-mono mb-1.5" data-testid="chat-status">
            {i === draft.statuses.length - 1 && !draft.text ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {s}
          </p>
        ))}
        {draft.text ? <Markdown text={draft.text} /> : (
          draft.statuses.length === 0 && <p className="flex items-center gap-2 text-xs text-sand-400 font-mono"><Loader2 size={12} className="animate-spin" /> Thinking…</p>
        )}
        <ChatArtifacts artifacts={draft.artifacts} messageId={null} onProposalAction={() => {}} onCancelAction={() => {}} onActionAction={() => {}} />
      </div>
    </div>
  );
}

export default function ConversationView({ sessionId, onSessionCreated, onAssistantDone, getContext, variant = "full", prefill = "" }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const composerRef = useRef(null);
  const createdSidRef = useRef(null);
  const panel = variant === "panel";

  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 200;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [input]);

  useEffect(() => {
    if (prefill) {
      setInput(prefill);
      composerRef.current?.focus();
    }
  }, [prefill]);

  useEffect(() => {
    setDraft(null);
    if (!sessionId) { setMessages([]); return; }
    if (createdSidRef.current === sessionId) { createdSidRef.current = null; return; }
    let alive = true;
    api.get(`/chat/sessions/${sessionId}/messages`)
      .then(({ data }) => { if (alive) setMessages(data); })
      .catch(() => { if (alive) setMessages([]); });
    return () => { alive = false; };
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, draft]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (attachments.length >= 4) { toast.error("Max 4 attachments per message"); break; }
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} is over 8MB`); continue; }
      if (!["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"].includes(file.type)) {
        toast.error(`${file.name}: only images and PDFs`); continue;
      }
      const temp = { id: `tmp-${Date.now()}-${file.name}`, filename: file.name, uploading: true };
      setAttachments((a) => [...a, temp]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/chat/attachments", fd);
        setAttachments((a) => a.map((x) => (x.id === temp.id ? { ...data, uploading: false } : x)));
      } catch {
        toast.error(`Upload failed: ${file.name}`);
        setAttachments((a) => a.filter((x) => x.id !== temp.id));
      }
    }
  };

  const patchArtifacts = (messageId, key, index, patch) => {
    setMessages((ms) => ms.map((m) => {
      if (m.id !== messageId) return m;
      const props = [...((m.artifacts || {})[key] || [])];
      props[index] = { ...props[index], ...patch };
      return { ...m, artifacts: { ...m.artifacts, [key]: props } };
    }));
  };

  const proposalAction = async (messageId, index, action) => {
    try {
      await api.post(`/chat/messages/${messageId}/wishlist-${action}`, { index });
      patchArtifacts(messageId, "wishlist_proposals", index, { approved: action === "approve", dismissed: action === "dismiss" });
      if (action === "approve") toast.success("Added to wishlist");
    } catch {
      toast.error("Action failed — try again");
    }
  };

  const cancelAction = async (messageId, index, action) => {
    try {
      await api.post(`/chat/messages/${messageId}/cancel-${action}`, { index });
      patchArtifacts(messageId, "cancellation_proposals", index, { confirmed: action === "approve", dismissed: action === "dismiss" });
      if (action === "approve") toast.success("Reservation marked cancelled");
    } catch {
      toast.error("Action failed — try again");
    }
  };

  const actionAction = async (messageId, index, action) => {
    try {
      await api.post(`/chat/messages/${messageId}/action-${action}`, { index });
      patchArtifacts(messageId, "action_proposals", index, { confirmed: action === "approve", dismissed: action === "dismiss" });
      if (action === "approve") toast.success("Done — trips updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const send = async (preset) => {
    const content = (preset || input).trim() || (attachments.length > 0 ? "See attached." : "");
    if (!content || sending) return;
    if (attachments.some((a) => a.uploading)) { toast.info("Wait for uploads to finish"); return; }
    const attachmentIds = attachments.map((a) => a.id);
    const attachmentMeta = attachments.map(({ id, filename, media_type, size }) => ({ id, filename, media_type, size }));
    setSending(true);
    setInput("");
    setAttachments([]);
    let sid = sessionId;
    try {
      if (!sid) {
        const { data } = await api.post("/chat/sessions", {});
        sid = data.id;
        createdSidRef.current = sid;
        onSessionCreated?.(data);
      }
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content, attachments: attachmentMeta }]);
      setDraft({ text: "", statuses: [], artifacts: null });

      const res = await fetchWithAuth(`${API_BASE}/chat/sessions/${sid}/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, attachment_ids: attachmentIds, context: getContext ? getContext() : undefined }),
      });
      if (!res.ok || !res.body) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* not json */ }
        if (res.status === 429 && detail?.code === "plan_limit") {
          setDraft(null);
          setPaywall(detail);
          setMessages((m) => m.filter((x) => x.role !== "user" || !String(x.id).startsWith("u-") || x.content !== content));
          setInput(content);
          return;
        }
        throw new Error("stream failed");
      }

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let finished = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          for (const line of chunk.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            const evt = JSON.parse(line.slice(6));
            if (evt.type === "delta") setDraft((d) => d && { ...d, text: d.text + evt.text });
            else if (evt.type === "status") setDraft((d) => d && { ...d, statuses: [...d.statuses, evt.label] });
            else if (evt.type === "artifacts") setDraft((d) => d && { ...d, artifacts: { flight_offers: evt.flight_offers, stays: evt.stays, wishlist_proposals: evt.wishlist_proposals, cancellation_proposals: evt.cancellation_proposals, action_proposals: evt.action_proposals, sources: evt.sources } });
            else if (evt.type === "done") {
              setMessages((m) => [...m, evt.message]);
              setDraft(null);
              finished = true;
              onAssistantDone?.();
            } else if (evt.type === "error") {
              toast.error(evt.detail);
              setDraft(null);
              finished = true;
            }
          }
        }
      }
      if (!finished) setDraft(null);
    } catch (e) {
      toast.error("Connection dropped — please try again");
      setDraft(null);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid={panel ? "assistant-conversation" : "chat-conversation"}>
      <div className="flex-1 overflow-y-auto">
        <div className={`${panel ? "px-3 py-4" : "max-w-3xl mx-auto px-4 md:px-6 py-6"} space-y-4`}>
          {messages.length === 0 && !draft && (
            <div className={`${panel ? "pt-6" : "pt-12"} text-center`} data-testid="chat-empty-state">
              <p className="label-tag mb-3">AI Planner</p>
              <h1 className={`heading-h2 ${panel ? "text-xl" : "text-2xl sm:text-3xl"} mb-2`}>Where should we go?</h1>
              <p className="text-sm text-sand-500 mb-8 max-w-md mx-auto">
                Live flights & hotels via Duffel, ranked by your travel profile. Hard rules are never violated.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center flex-wrap">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="btn-swiss btn-outline text-xs" data-testid="chat-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => <Message key={m.id} msg={m} onProposalAction={proposalAction} onCancelAction={cancelAction} onActionAction={actionAction} />)}
          {paywall && (
            <div className="flex" data-testid="chat-paywall">
              <div className="max-w-[92%] md:max-w-[85%] bg-surface rounded-card border border-ember/40 px-4 py-3">
                <p className="flex items-center gap-2 text-xs font-mono text-ember mb-1.5">
                  <Sparkles size={12} /> FREE PLAN LIMIT
                </p>
                <p className="text-sm text-sand-800">{paywall.message}</p>
                <p className="text-xs text-sand-500 mt-1.5">Your allowance resets on the 1st of next month.</p>
              </div>
            </div>
          )}
          {draft && <Draft draft={draft} />}
          <div ref={bottomRef} />
        </div>
      </div>
      <div className="border-t border-sand-200 bg-surface shrink-0">
        <div className={`${panel ? "px-3" : "max-w-3xl mx-auto px-4 md:px-6"} py-3`}>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="composer-attachments">
              {attachments.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1.5 bg-sand-100 border border-sand-200 px-2 py-1 text-[11px] font-mono">
                  {a.uploading ? <Loader2 size={10} className="animate-spin" /> : <FileText size={10} className="text-ember" />}
                  {a.filename}
                  <button onClick={() => setAttachments((x) => x.filter((y) => y.id !== a.id))}
                    className="text-sand-400 hover:text-sand-900" data-testid={`attachment-remove-${a.id}`}>
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <input ref={fileRef} type="file" multiple className="hidden"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              onChange={onFiles} data-testid="chat-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={sending}
              className="btn-swiss btn-ghost text-sm shrink-0 disabled:opacity-50" title="Attach images or PDFs"
              data-testid="chat-attach-btn">
              <Paperclip size={16} />
            </button>
            <textarea
              ref={composerRef}
              className="flex-1 border border-sand-300 bg-surface px-3 py-2.5 text-sm resize-none leading-relaxed focus:outline-none focus:border-ember transition-colors"
              rows={1}
              placeholder="Ask me to plan a trip…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              data-testid={panel ? "assistant-input" : "chat-input"}
            />
            <button onClick={() => send()} disabled={sending || (!input.trim() && attachments.length === 0)}
              className="btn-swiss btn-primary text-sm disabled:opacity-50 shrink-0" data-testid={panel ? "assistant-send-btn" : "chat-send-btn"}>
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
